import React, { useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";
import api, { ROLE_LABELS } from "@/lib/api";
import useDraft from "@/lib/useDraft";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, IndianRupee, Plus, Pencil, Trash2, Settings, X, AlertTriangle, TrendingUp, Clock, Download, Mail } from "lucide-react";

const TEAM_ROLES = ["admin", "manager", "lead", "employee"];
const ROLES = ["admin", "manager", "leader", "sr_engineer", "jr_engineer", "drafter", "qa1", "qa2", "final_qc", "hr_manager", "office_assistant"];

export default function Attendance() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || (user?.team_role || "").toLowerCase() === "admin";
  const [tab, setTab] = useState("employees");
  const [month, setMonth] = useState(() => new Date().toISOString().slice(0, 7));

  return (
    <div className="space-y-5 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">HUMAN RESOURCES</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Attendance & People</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">
            Employees · Attendance · Utilization{isAdmin ? " · Payroll" : ""}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-zinc-900 overflow-x-auto">
        <TabBtn active={tab === "employees"} onClick={() => setTab("employees")} testid="tab-employees">EMPLOYEES</TabBtn>
        <TabBtn active={tab === "records"} onClick={() => setTab("records")} testid="tab-records">ATTENDANCE</TabBtn>
        <TabBtn active={tab === "utilization"} onClick={() => setTab("utilization")} testid="tab-utilization">UTILIZATION</TabBtn>
        {isAdmin && <TabBtn active={tab === "payroll"} onClick={() => setTab("payroll")} testid="tab-payroll">PAYROLL</TabBtn>}
        {isAdmin && <TabBtn active={tab === "rules"} onClick={() => setTab("rules")} testid="tab-rules">RULES</TabBtn>}
        <TabBtn active={tab === "myslips"} onClick={() => setTab("myslips")} testid="tab-my-payslips">MY PAYSLIPS</TabBtn>
      </div>

      {tab === "employees" && <EmployeesTab isAdmin={isAdmin} />}
      {tab === "records" && <RecordsTab />}
      {tab === "utilization" && <UtilizationTab month={month} setMonth={setMonth} />}
      {tab === "payroll" && isAdmin && <PayrollTab month={month} setMonth={setMonth} />}
      {tab === "rules" && isAdmin && <RulesTab />}
      {tab === "myslips" && <MyPayslipsTab />}
    </div>
  );
}

const TabBtn = ({ active, onClick, children, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`px-4 py-2 text-sm font-mono font-bold border-t border-l border-r border-zinc-900 -mb-px whitespace-nowrap ${active ? "bg-yellow-200" : "bg-white"}`}>
    {children}
  </button>
);

// ============================== EMPLOYEES ==============================
function EmployeesTab({ isAdmin }) {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [filterRole, setFilterRole] = useState("all");
  const [includeInactive, setIncludeInactive] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get("/users", { params: includeInactive ? { include_inactive: true } : {} });
    setUsers(data || []);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [includeInactive]);

  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      // Use the auto-detecting endpoint so BOTH the SalaryBox Bulk-Update
      // Employee sheet AND the SalaryBox Monthly Wage salary template work
      // from the same button. The backend inspects the sheet name + column
      // headers to pick the right alias map.
      const { data } = await api.post("/employees/import-hr-master", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const errCount = (data.errors || []).length;
      if (data.created === 0 && data.updated === 0 && errCount > 0) {
        const firstErr = data.errors[0];
        const msg = typeof firstErr === "string" ? firstErr : (firstErr?.error || JSON.stringify(firstErr));
        toast.error(`Import failed: 0 imported, ${errCount} errors. First: ${msg.slice(0, 200)}`);
      } else {
        toast.success(`Imported ${data.created} new, ${data.updated} updated${errCount ? ` (${errCount} errors)` : ""}`);
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    e.target.value = "";
  };

  const downloadFile = async (url, name) => {
    const token = localStorage.getItem("session_token");
    const res = await api.get(url, { params: token ? { auth: token } : {}, responseType: "blob" });
    const u = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = u; a.download = name; a.click();
    URL.revokeObjectURL(u);
  };

  const remove = async (u) => {
    if (!confirm(`Mark "${u.name}" as inactive (date_of_leaving will be set to today)?`)) return;
    await api.delete(`/employees/${u.user_id}`);
    toast.success("Employee deactivated"); load();
  };

  const filtered = users.filter((u) => {
    if (filterRole !== "all" && (u.team_role || "employee") !== filterRole) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return [u.name, u.email, u.employee_code, u.employee_id, u.team].some((v) => (v || "").toLowerCase().includes(s));
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name / email / code / team..."
            className="brut-input px-3 py-2 text-sm w-72" data-testid="emp-search-input" />
          <select value={filterRole} onChange={(e) => setFilterRole(e.target.value)} className="brut-input px-2 py-2 text-sm font-mono" data-testid="emp-filter-role">
            <option value="all">All team roles</option>
            {TEAM_ROLES.map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}
          </select>
          <label className="flex items-center gap-1.5 text-xs font-mono">
            <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} data-testid="emp-include-inactive" />
            Include inactive
          </label>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && (
            <>
              <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onImport} className="hidden" data-testid="emp-import-input" />
              <button onClick={() => setShowNew(true)} className="brut-btn px-3 py-2 text-xs flex items-center gap-1" data-testid="emp-add-btn"><Plus className="w-3.5 h-3.5" /> NEW EMPLOYEE</button>
              <button onClick={() => fileRef.current?.click()} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="emp-import-btn"><Upload className="w-3.5 h-3.5" /> IMPORT SALARYBOX</button>
              <button onClick={() => downloadFile("/employees/template", "employees_salarybox_template.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="emp-template-btn"><FileSpreadsheet className="w-3.5 h-3.5" /> TEMPLATE</button>
              <button onClick={() => downloadFile("/employees/export?format=xlsx", "employees.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs" data-testid="emp-export-btn">EXPORT</button>
              <button
                onClick={async () => {
                  if (!confirm("Export team WITHOUT salaries.\n\nThis will RESET each active member's login password and include the new password in the exported file.\n\nProceed?")) return;
                  try {
                    const token = localStorage.getItem("session_token");
                    const res = await api.post(
                      "/employees/export-no-salary",
                      null,
                      { params: { format: "xlsx", reset_passwords: true, ...(token ? { auth: token } : {}) }, responseType: "blob" }
                    );
                    const u = URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a"); a.href = u; a.download = "team_with_passwords.xlsx"; a.click();
                    URL.revokeObjectURL(u);
                    toast.success("Export ready — passwords reset and included in file");
                  } catch (err) { toast.error(err.response?.data?.detail || "Export failed"); }
                }}
                className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-yellow-300 hover:bg-yellow-400"
                data-testid="emp-export-no-salary-btn"
                title="Export team details without salaries; resets and includes login passwords"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" /> EXPORT + PASSWORDS
              </button>
              <button
                onClick={async () => {
                  if (!confirm("Export FULL team data including SALARIES and a freshly-generated password per user.\n\nThis will RESET every active member's login password.\n\nProceed?")) return;
                  try {
                    const token = localStorage.getItem("session_token");
                    const res = await api.post(
                      "/employees/export-full",
                      null,
                      { params: { format: "xlsx", reset_passwords: true, ...(token ? { auth: token } : {}) }, responseType: "blob" }
                    );
                    const u = URL.createObjectURL(new Blob([res.data]));
                    const a = document.createElement("a"); a.href = u; a.download = "team_full_with_passwords.xlsx"; a.click();
                    URL.revokeObjectURL(u);
                    toast.success("Full export ready — salaries + passwords included");
                  } catch (err) { toast.error(err.response?.data?.detail || "Export failed"); }
                }}
                className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-orange-300 hover:bg-orange-400"
                data-testid="emp-export-full-btn"
                title="Export every HR field including salaries and freshly-reset passwords (admin only)"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" /> FULL EXPORT + SALARY + PASSWORDS
              </button>
            </>
          )}
        </div>
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-zinc-900 text-white">
              <Th>Code</Th><Th>Name</Th><Th>Email</Th><Th>Mobile</Th><Th>Role</Th><Th>Combined</Th><Th>Team</Th><Th>Team Role</Th><Th>DOJ</Th><Th align="right">CTC</Th><Th align="right">Monthly</Th><Th align="center">PF</Th><Th align="center">ESIC</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={14} className="text-center py-10 text-zinc-400 font-mono text-xs">NO EMPLOYEES — ADD ONE OR IMPORT SALARYBOX TEMPLATE</td></tr>
            )}
            {filtered.map((u) => (
              <tr key={u.user_id} className={`border-b border-zinc-200 hover:bg-zinc-50 ${u.is_active === false ? "opacity-50" : ""}`} data-testid={`emp-row-${u.user_id}`}>
                <Td><span className="font-mono text-xs">{u.employee_code || u.employee_id || "—"}</span></Td>
                <Td className="font-bold">{u.name}</Td>
                <Td><span className="font-mono text-xs">{u.email}</span></Td>
                <Td><span className="font-mono text-xs">{u.mobile || "—"}</span></Td>
                <Td><span className="font-mono text-xs">{ROLE_LABELS[u.role] || u.role}</span></Td>
                <Td><span className="font-mono text-xs">{(u.roles_combined || []).join(" + ") || "—"}</span></Td>
                <Td><span className="font-mono text-xs">{u.team || "—"}</span></Td>
                <Td><span className={`font-mono text-xs uppercase px-1.5 py-0.5 border border-zinc-900 ${u.team_role === "manager" ? "bg-orange-200" : u.team_role === "lead" ? "bg-blue-200" : u.team_role === "admin" ? "bg-red-200" : "bg-zinc-100"}`}>{u.team_role || "employee"}</span></Td>
                <Td><span className="font-mono text-xs">{u.date_of_joining || "—"}</span></Td>
                <Td align="right"><span className="font-mono">{(u.ctc_annual_inr || 0).toLocaleString("en-IN")}</span></Td>
                <Td align="right"><span className="font-mono font-bold">{(u.monthly_salary_inr || 0).toLocaleString("en-IN")}</span></Td>
                <Td align="center">{u.pf_enabled ? <span className="font-mono text-xs bg-green-100 border border-zinc-900 px-1.5 py-0.5">YES</span> : <span className="font-mono text-xs text-zinc-400">—</span>}</Td>
                <Td align="center">{u.esic_enabled ? <span className="font-mono text-xs bg-green-100 border border-zinc-900 px-1.5 py-0.5">YES</span> : <span className="font-mono text-xs text-zinc-400">—</span>}</Td>
                <Td align="right">
                  {isAdmin && (
                    <div className="flex gap-1 justify-end">
                      <button onClick={() => setEditing(u)} className="brut-btn-secondary p-1.5" data-testid={`emp-edit-${u.user_id}`}><Pencil className="w-3 h-3" /></button>
                      <button onClick={() => remove(u)} className="text-red-600 p-1.5"><Trash2 className="w-3 h-3" /></button>
                    </div>
                  )}
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {(showNew || editing) && (
        <EmployeeModal employee={editing} allUsers={users}
          onClose={() => { setShowNew(false); setEditing(null); }}
          onSaved={() => { setShowNew(false); setEditing(null); load(); }} />
      )}
    </div>
  );
}

function EmployeeModal({ employee, allUsers, onClose, onSaved }) {
  const draftKey = employee?.user_id || "new";
  const [form, setForm, clearDraft] = useDraft("employee", draftKey, {
    email: "", name: "", employee_code: "", role: "jr_engineer", roles_combined: [],
    team: "", team_role: "employee", manager_id: "",
    primary_team: "", co_primary_team: "", shared_teams: [],
    mobile: "", personal_email: "", gender: "", date_of_birth: "", date_of_joining: "", date_of_leaving: "",
    address: "",
    pan_number: "", aadhaar_number: "",
    bank_account_number: "", bank_ifsc: "", bank_name: "",
    pf_enabled: false, pf_number: "", uan_number: "", pf_rate: 12,
    esic_enabled: false, esic_number: "", esic_employee_rate: 0.75, esic_employer_rate: 3.25,
    ctc_annual_inr: 0, basic_salary_inr: 0, hra_inr: 0, special_allowance_inr: 0, other_allowance_inr: 0,
    monthly_salary_inr: 0, is_active: true,
    ...(employee || {}),
  });
  const [saving, setSaving] = useState(false);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (employee) {
        await api.patch(`/users/${employee.user_id}`, form);
      } else {
        await api.post("/employees", form);
      }
      toast.success(employee ? "Employee updated" : "Employee created");
      clearDraft();
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };

  const toggleCombined = (r) => {
    const set = new Set(form.roles_combined || []);
    if (set.has(r)) set.delete(r); else set.add(r);
    upd("roles_combined", Array.from(set));
  };

  return createPortal((
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-start justify-center p-2 sm:p-4 pt-[76px] overflow-y-auto">
      <div className="brut-card max-w-4xl w-full flex flex-col min-h-0" style={{ maxHeight: "calc(100vh - 32px)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-900 bg-white shrink-0">
          <div>
            <h2 className="font-heading text-xl font-black">{employee ? "Edit Employee" : "New Employee"}</h2>
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">DRAFT AUTO-SAVED · Outside-click won't dismiss</div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => { if (confirm("Discard unsaved draft for this employee form?")) { clearDraft(); onClose(); } }}
              className="brut-btn-secondary px-2 py-1 text-[10px] font-mono"
              title="Discard the auto-saved draft and close"
            >DISCARD</button>
            <button onClick={onClose} className="brut-btn-secondary p-1.5"><X className="w-4 h-4" /></button>
          </div>
        </div>
        <form id="emp-form" onSubmit={submit} className="px-5 py-4 overflow-y-auto flex-1 min-h-0 space-y-4">
          <Section title="IDENTITY">
            <Field label="EMPLOYEE CODE"><input className="brut-input w-full px-3 py-1.5 text-sm" value={form.employee_code || ""} onChange={(e) => upd("employee_code", e.target.value)} data-testid="emp-code-input" /></Field>
            <Field label="FULL NAME" required><input required className="brut-input w-full px-3 py-1.5 text-sm" value={form.name || ""} onChange={(e) => upd("name", e.target.value)} data-testid="emp-name-input" /></Field>
            <Field label="WORK EMAIL" required><input required type="email" disabled={!!employee} className="brut-input w-full px-3 py-1.5 text-sm font-mono disabled:opacity-60" value={form.email || ""} onChange={(e) => upd("email", e.target.value)} data-testid="emp-email-input" /></Field>
            <Field label="PERSONAL EMAIL"><input type="email" className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.personal_email || ""} onChange={(e) => upd("personal_email", e.target.value)} /></Field>
            <Field label="MOBILE"><input className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.mobile || ""} onChange={(e) => upd("mobile", e.target.value)} /></Field>
            <Field label="GENDER"><select className="brut-input w-full px-3 py-1.5 text-sm" value={form.gender || ""} onChange={(e) => upd("gender", e.target.value)}><option value="">—</option><option>Male</option><option>Female</option><option>Other</option></select></Field>
            <Field label="DOB"><input type="date" className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.date_of_birth || ""} onChange={(e) => upd("date_of_birth", e.target.value)} /></Field>
            <Field label="DATE OF JOINING"><input type="date" className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.date_of_joining || ""} onChange={(e) => upd("date_of_joining", e.target.value)} /></Field>
            <Field label="ADDRESS" full><textarea rows={2} className="brut-input w-full px-3 py-1.5 text-sm" value={form.address || ""} onChange={(e) => upd("address", e.target.value)} /></Field>
          </Section>

          <Section title="HIERARCHY & ROLE">
            <div className="md:col-span-3 -mb-1 flex items-center gap-2 flex-wrap text-[10px] font-mono text-zinc-600">
              <span className="uppercase tracking-wider">SurveyDrafts.us controls this section</span>
              <SourceBadge src="sd_sync" />
              <SourceBadge src="admin" />
              <SourceBadge src="" />
              <span className="text-zinc-500">— editing any field freezes it until you clear the override.</span>
            </div>
            <Field label={<span>PRIMARY ROLE <SourceBadge src={form.role_source} /></span>}><select className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.role} onChange={(e) => upd("role", e.target.value)}>{ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}</select></Field>
            <Field label={<span>TEAM ROLE <SourceBadge src={form.role_source} /></span>}><select className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.team_role || "employee"} onChange={(e) => upd("team_role", e.target.value)} data-testid="emp-team-role-select">{TEAM_ROLES.map((r) => <option key={r} value={r}>{r.toUpperCase()}</option>)}</select></Field>
            <Field label={<span>HOME TEAM <SourceBadge src={form.team_source} /></span>}><input className="brut-input w-full px-3 py-1.5 text-sm" placeholder="Drafting-A" value={form.team || ""} onChange={(e) => upd("team", e.target.value)} data-testid="emp-home-team-input" /></Field>
            <Field label="REPORTS TO (Manager)"><select className="brut-input w-full px-3 py-1.5 text-sm" value={form.manager_id || ""} onChange={(e) => upd("manager_id", e.target.value)} data-testid="emp-manager-select"><option value="">— None —</option>{(allUsers || []).filter((u) => u.user_id !== employee?.user_id).map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({u.team_role || "employee"})</option>)}</select></Field>
            <Field label={<span>PRIMARY TEAM (visibility) <SourceBadge src={form.primary_team_source} /></span>}><input className="brut-input w-full px-3 py-1.5 text-sm" placeholder="e.g. Team_LD_02" value={form.primary_team || ""} onChange={(e) => upd("primary_team", e.target.value)} data-testid="emp-primary-team-input" /></Field>
            <Field label="CO-PRIMARY TEAM (visibility)"><input className="brut-input w-full px-3 py-1.5 text-sm" placeholder="e.g. Team_LD_03_DAY" value={form.co_primary_team || ""} onChange={(e) => upd("co_primary_team", e.target.value)} data-testid="emp-coprimary-team-input" /></Field>
            <Field label="SHARED TEAMS (comma-sep, visibility)" full>
              <input
                className="brut-input w-full px-3 py-1.5 text-sm font-mono"
                placeholder="e.g. Team_LD_04_DAY, Team_LD_05"
                value={Array.isArray(form.shared_teams) ? form.shared_teams.join(", ") : (form.shared_teams || "")}
                onChange={(e) => upd("shared_teams", e.target.value.split(",").map((s) => s.trim()).filter(Boolean))}
                data-testid="emp-shared-teams-input"
              />
              <div className="label-mono mt-1">User will see Jobs / Workflow / Leaderboard / Members from: home + primary + co-primary + shared teams.</div>
            </Field>
            <Field label="COMBINED ROLES" full>
              <div className="flex flex-wrap gap-1.5">
                {ROLES.map((r) => (
                  <button key={r} type="button" onClick={() => toggleCombined(r)}
                    className={`px-2 py-1 text-xs font-mono border border-zinc-900 ${(form.roles_combined || []).includes(r) ? "bg-yellow-200 font-bold" : "bg-white"}`}>
                    {ROLE_LABELS[r] || r}
                  </button>
                ))}
              </div>
              <div className="label-mono mt-1">Selected: {(form.roles_combined || []).map((r) => ROLE_LABELS[r] || r).join(" + ") || "—"}</div>
            </Field>
          </Section>

          <Section title="STATUTORY & BANK">
            <Field label="PAN"><input className="brut-input w-full px-3 py-1.5 text-sm font-mono uppercase" value={form.pan_number || ""} onChange={(e) => upd("pan_number", e.target.value.toUpperCase())} /></Field>
            <Field label="AADHAAR"><input className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.aadhaar_number || ""} onChange={(e) => upd("aadhaar_number", e.target.value)} /></Field>
            <Field label="BANK ACCOUNT NO."><input className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.bank_account_number || ""} onChange={(e) => upd("bank_account_number", e.target.value)} /></Field>
            <Field label="IFSC"><input className="brut-input w-full px-3 py-1.5 text-sm font-mono uppercase" value={form.bank_ifsc || ""} onChange={(e) => upd("bank_ifsc", e.target.value.toUpperCase())} /></Field>
            <Field label="BANK NAME"><input className="brut-input w-full px-3 py-1.5 text-sm" value={form.bank_name || ""} onChange={(e) => upd("bank_name", e.target.value)} /></Field>
          </Section>

          <Section title="PF / ESIC">
            <Field label="PF ENABLED">
              <label className="flex items-center gap-2 mt-1.5"><input type="checkbox" checked={!!form.pf_enabled} onChange={(e) => upd("pf_enabled", e.target.checked)} data-testid="emp-pf-toggle" /> <span className="text-sm font-mono">Deduct PF from this employee</span></label>
            </Field>
            <Field label="PF RATE %"><input type="number" step="0.01" disabled={!form.pf_enabled} className="brut-input w-full px-3 py-1.5 text-sm font-mono disabled:opacity-50" value={form.pf_rate ?? 12} onChange={(e) => upd("pf_rate", parseFloat(e.target.value) || 0)} /></Field>
            <Field label="PF NUMBER"><input disabled={!form.pf_enabled} className="brut-input w-full px-3 py-1.5 text-sm font-mono disabled:opacity-50" value={form.pf_number || ""} onChange={(e) => upd("pf_number", e.target.value)} /></Field>
            <Field label="UAN"><input disabled={!form.pf_enabled} className="brut-input w-full px-3 py-1.5 text-sm font-mono disabled:opacity-50" value={form.uan_number || ""} onChange={(e) => upd("uan_number", e.target.value)} /></Field>
            <Field label="ESIC ENABLED">
              <label className="flex items-center gap-2 mt-1.5"><input type="checkbox" checked={!!form.esic_enabled} onChange={(e) => upd("esic_enabled", e.target.checked)} data-testid="emp-esic-toggle" /> <span className="text-sm font-mono">Eligible (gross ≤ ₹21,000)</span></label>
            </Field>
            <Field label="ESIC NO."><input disabled={!form.esic_enabled} className="brut-input w-full px-3 py-1.5 text-sm font-mono disabled:opacity-50" value={form.esic_number || ""} onChange={(e) => upd("esic_number", e.target.value)} /></Field>
            <Field label="ESIC EMPLOYEE %"><input type="number" step="0.01" disabled={!form.esic_enabled} className="brut-input w-full px-3 py-1.5 text-sm font-mono disabled:opacity-50" value={form.esic_employee_rate ?? 0.75} onChange={(e) => upd("esic_employee_rate", parseFloat(e.target.value) || 0)} /></Field>
            <Field label="ESIC EMPLOYER %"><input type="number" step="0.01" disabled={!form.esic_enabled} className="brut-input w-full px-3 py-1.5 text-sm font-mono disabled:opacity-50" value={form.esic_employer_rate ?? 3.25} onChange={(e) => upd("esic_employer_rate", parseFloat(e.target.value) || 0)} /></Field>
          </Section>

          <Section title="COMPENSATION (INR)">
            <Field label="CTC ANNUAL"><input type="number" className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.ctc_annual_inr || 0} onChange={(e) => upd("ctc_annual_inr", parseFloat(e.target.value) || 0)} data-testid="emp-ctc-input" /></Field>
            <Field label="MONTHLY GROSS"><input type="number" className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.monthly_salary_inr || 0} onChange={(e) => upd("monthly_salary_inr", parseFloat(e.target.value) || 0)} data-testid="emp-monthly-input" /></Field>
            <Field label="BASIC"><input type="number" className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.basic_salary_inr || 0} onChange={(e) => upd("basic_salary_inr", parseFloat(e.target.value) || 0)} /></Field>
            <Field label="HRA"><input type="number" className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.hra_inr || 0} onChange={(e) => upd("hra_inr", parseFloat(e.target.value) || 0)} /></Field>
            <Field label="SPECIAL ALLOWANCE"><input type="number" className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.special_allowance_inr || 0} onChange={(e) => upd("special_allowance_inr", parseFloat(e.target.value) || 0)} /></Field>
            <Field label="OTHER ALLOWANCE"><input type="number" className="brut-input w-full px-3 py-1.5 text-sm font-mono" value={form.other_allowance_inr || 0} onChange={(e) => upd("other_allowance_inr", parseFloat(e.target.value) || 0)} /></Field>
          </Section>
        </form>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-zinc-900 bg-white shrink-0">
          <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
          <button type="submit" form="emp-form" disabled={saving} className="brut-btn px-4 py-2 text-sm disabled:opacity-50" data-testid="emp-save-btn">{saving ? "SAVING..." : (employee ? "SAVE" : "CREATE")}</button>
        </div>
      </div>
    </div>
  ), document.body);
}

const Section = ({ title, children }) => (
  <div>
    <div className="label-mono mb-2 text-zinc-500 border-b border-zinc-200 pb-1">{title}</div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">{children}</div>
  </div>
);

const Field = ({ label, children, full, required }) => (
  <label className={`block ${full ? "md:col-span-3" : ""}`}>
    <div className="label-mono mb-1 text-xs">{label}{required && <span className="text-red-600"> *</span>}</div>
    {children}
  </label>
);

// Small provenance badge next to fields that are managed by SurveyDrafts sync.
// Renders one of three states so admins can see at a glance which fields will
// be overwritten on the next SD sync vs which they've manually frozen.
const SourceBadge = ({ src }) => {
  if (!src) return <span className="ml-1 px-1 py-[1px] text-[9px] font-mono bg-zinc-100 border border-zinc-400 text-zinc-500 uppercase" title="Never synced from SurveyDrafts — will be set on the next sync if this user's email matches an SD member.">not synced</span>;
  const s = String(src).toLowerCase();
  if (s === "admin") return <span className="ml-1 px-1 py-[1px] text-[9px] font-mono bg-orange-100 border border-orange-700 text-orange-900 uppercase" title="Manually overridden — SurveyDrafts sync will NOT touch this field until you save a blank value or use the reset control.">manual override</span>;
  if (s === "sd_sync") return <span className="ml-1 px-1 py-[1px] text-[9px] font-mono bg-emerald-100 border border-emerald-700 text-emerald-900 uppercase" title="Auto-synced from SurveyDrafts. Will be refreshed on the next sync.">synced from sd</span>;
  return <span className="ml-1 px-1 py-[1px] text-[9px] font-mono bg-zinc-100 border border-zinc-400 text-zinc-500 uppercase">{s}</span>;
};

// ============================== RECORDS ==============================
function RecordsTab() {
  const [rows, setRows] = useState([]);
  const fileRef = useRef(null);
  const load = async () => { const { data } = await api.get("/attendance"); setRows(data); };
  useEffect(() => { load(); }, []);

  const onImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/attendance/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} rows`); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    e.target.value = "";
  };
  const downloadFile = async (url, name) => {
    const token = localStorage.getItem("session_token");
    const res = await api.get(url, { params: token ? { auth: token } : {}, responseType: "blob" });
    const u = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = u; a.download = name; a.click(); URL.revokeObjectURL(u);
  };
  const statusColor = (s) => ({ present: "bg-green-200", absent: "bg-red-200", leave: "bg-amber-200", half_day: "bg-yellow-200" })[s] || "bg-zinc-200";

  return (
    <div className="space-y-3">
      <div className="flex justify-end gap-2 flex-wrap">
        <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onImport} className="hidden" data-testid="attendance-import-input" />
        <button onClick={() => fileRef.current?.click()} className="brut-btn px-3 py-2 text-xs flex items-center gap-1" data-testid="attendance-import-btn"><Upload className="w-3.5 h-3.5" /> IMPORT EXCEL/CSV</button>
        <button onClick={() => downloadFile("/attendance/template", "attendance_template.xlsx")} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="attendance-template-btn"><FileSpreadsheet className="w-3.5 h-3.5" /> TEMPLATE</button>
      </div>
      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead><tr className="bg-zinc-900 text-white"><Th>Date</Th><Th>Name</Th><Th>Emp ID</Th><Th>In</Th><Th>Out</Th><Th align="right">Hours</Th><Th align="center">Late</Th><Th align="center">Early-Out</Th><Th>Status</Th></tr></thead>
          <tbody>
            {rows.length === 0 && (<tr><td colSpan={9} className="text-center py-10 text-zinc-400 font-mono text-xs">NO ATTENDANCE RECORDS — IMPORT EXCEL TO BEGIN</td></tr>)}
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                <Td><span className="font-mono">{r.date}</span></Td>
                <Td className="font-bold">{r.user_name}</Td>
                <Td><span className="font-mono text-xs">{r.employee_id || "—"}</span></Td>
                <Td><span className="font-mono">{r.check_in || "—"}</span></Td>
                <Td><span className="font-mono">{r.check_out || "—"}</span></Td>
                <Td align="right"><span className="font-mono">{r.hours_worked}h</span></Td>
                <Td align="center">{r.is_late ? <span className="font-mono text-xs bg-red-200 border border-zinc-900 px-1.5 py-0.5 font-bold">LATE</span> : <span className="text-zinc-400 text-xs">—</span>}</Td>
                <Td align="center">{r.is_early_out ? <span className="font-mono text-xs bg-orange-200 border border-zinc-900 px-1.5 py-0.5 font-bold">EARLY</span> : <span className="text-zinc-400 text-xs">—</span>}</Td>
                <Td><span className={`font-mono text-xs uppercase font-bold px-2 py-0.5 border border-zinc-900 ${statusColor(r.status)}`}>{r.status}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================== UTILIZATION ==============================
function UtilizationTab({ month, setMonth }) {
  const [data, setData] = useState(null);
  const [expHrs, setExpHrs] = useState(8);
  const load = async () => {
    const { data } = await api.get("/team/utilization", { params: { month, expected_hours_per_day: expHrs } });
    setData(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, expHrs]);

  if (!data) return <div className="font-mono text-xs text-zinc-500">Loading utilization...</div>;
  const utilColor = (p) => p >= 90 ? "bg-green-200" : p >= 70 ? "bg-lime-200" : p >= 50 ? "bg-yellow-200" : p > 0 ? "bg-orange-200" : "bg-red-200";

  return (
    <div className="space-y-3">
      <div className="brut-card p-4 flex items-end justify-between flex-wrap gap-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div><div className="label-mono">MONTH</div><input type="month" className="brut-input px-3 py-1.5 text-sm font-mono mt-1" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="util-month-input" /></div>
          <div><div className="label-mono">EXPECTED HRS/DAY</div><input type="number" step="0.5" min="1" className="brut-input px-3 py-1.5 text-sm font-mono mt-1 w-24" value={expHrs} onChange={(e) => setExpHrs(parseFloat(e.target.value) || 8)} /></div>
        </div>
        <div className="text-right text-xs font-mono text-zinc-500 max-w-md">
          Utilization = sum of stage-time logged on assigned jobs ÷ attendance hours · low values flag unaccounted time.
        </div>
      </div>

      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-zinc-900 text-white">
              <Th>Employee</Th><Th>Code</Th><Th>Team</Th>
              <Th align="center">Days P/H/L/A</Th>
              <Th align="center">Late</Th>
              <Th align="center">Early-Out</Th>
              <Th align="right">Expected (h)</Th>
              <Th align="right">Attendance (h)</Th>
              <Th align="right">Stage Hours</Th>
              <Th align="right">Daily Entry (h)</Th>
              <Th align="right">Variance (h)</Th>
              <Th align="right">Utilization %</Th>
            </tr>
          </thead>
          <tbody>
            {(data.rows || []).length === 0 && (<tr><td colSpan={12} className="text-center py-10 text-zinc-400 font-mono text-xs">NO DATA FOR {data.month}</td></tr>)}
            {(data.rows || []).map((r) => (
              <tr key={r.user_id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`util-row-${r.user_id}`}>
                <Td className="font-bold">{r.user_name}</Td>
                <Td><span className="font-mono text-xs">{r.employee_code || "—"}</span></Td>
                <Td><span className="font-mono text-xs">{r.team || "—"}</span></Td>
                <Td align="center"><span className="font-mono text-xs">{r.days_present}/{r.days_half}/{r.days_leave}/{r.days_absent}</span></Td>
                <Td align="center">{r.late_marks > 0 ? <span className="font-mono text-xs bg-red-100 border border-zinc-900 px-1 font-bold">{r.late_marks}</span> : <span className="text-zinc-400 text-xs">—</span>}</Td>
                <Td align="center">{(r.early_out_marks || 0) > 0 ? <span className="font-mono text-xs bg-orange-100 border border-zinc-900 px-1 font-bold">{r.early_out_marks}</span> : <span className="text-zinc-400 text-xs">—</span>}</Td>
                <Td align="right"><span className="font-mono">{r.expected_hours}</span></Td>
                <Td align="right"><span className="font-mono font-bold">{r.attendance_hours}</span></Td>
                <Td align="right"><span className="font-mono">{r.job_stage_hours}</span></Td>
                <Td align="right"><span className="font-mono">{r.daily_entry_hours}</span></Td>
                <Td align="right"><span className={`font-mono ${r.variance_hours > 1 ? "text-orange-700 font-bold" : ""}`}>{r.variance_hours}</span></Td>
                <Td align="right"><span className={`font-mono font-bold px-2 py-0.5 border border-zinc-900 ${utilColor(r.utilization_pct)}`}>{r.utilization_pct}%</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============================== PAYROLL ==============================
function CtcReportsModal({ open, onClose, onPickMonth, onEmailMonth, includeEmployer }) {
  const [reports, setReports] = useState([]);
  const [selMonth, setSelMonth] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [zipping, setZipping] = useState(false);
  const downloadZip = async () => {
    if (!selMonth) return;
    setZipping(true);
    try {
      const res = await api.get("/attendance/payroll/payslips-zip", {
        params: { month: selMonth, include_employer_contribution: includeEmployer },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `Payslips_${selMonth}.zip`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Payslips ZIP for ${selMonth} downloaded`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to generate payslips ZIP"); }
    setZipping(false);
  };
  useEffect(() => {
    if (!open) return;
    api.get("/attendance/payroll/ctc-reports").then(({ data }) => {
      setReports(data.reports || []);
      if ((data.reports || []).length && !selMonth) loadRows(data.reports[0].month);
    }).catch(() => toast.error("Failed to load CTC reports"));
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const loadRows = async (m) => {
    setSelMonth(m); setLoading(true);
    try {
      const { data } = await api.get("/attendance/payroll/ctc-report-rows", { params: { month: m } });
      setRows(data.rows || []);
    } catch { toast.error("Failed to load report rows"); }
    setLoading(false);
  };
  if (!open) return null;
  const fmt = (v) => (v || v === 0 ? Number(v).toLocaleString("en-IN", { minimumFractionDigits: 2 }) : "—");
  const monthLabel = (m) => { const [y, mo] = m.split("-"); return new Date(y, mo - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" }); };
  return createPortal((
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-start justify-center p-4 pt-[64px] overflow-y-auto" data-testid="ctc-reports-modal">
      <div className="brut-card bg-white w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-zinc-900">
          <div className="font-heading font-black text-lg flex items-center gap-2"><FileSpreadsheet className="w-4 h-4" /> IMPORTED CTC REPORTS</div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5" data-testid="ctc-reports-close-btn"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex flex-1 min-h-0">
          <div className="w-56 border-r-2 border-zinc-900 overflow-y-auto">
            {reports.map((r) => (
              <button key={r.month} onClick={() => loadRows(r.month)}
                className={`w-full text-left px-3 py-2.5 border-b border-zinc-200 hover:bg-emerald-50 ${selMonth === r.month ? "bg-emerald-100 border-l-4 border-l-emerald-700" : ""}`}
                data-testid={`ctc-report-month-${r.month}`}>
                <div className="font-bold text-sm">{monthLabel(r.month)}</div>
                <div className="font-mono text-[10px] text-zinc-500">{r.count} emp · ₹{fmt(r.total_gross_inr)}</div>
                <div className="font-mono text-[10px] text-zinc-400">{r.imported_at ? new Date(r.imported_at).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}{r.imported_by ? ` · ${r.imported_by.split("@")[0]}` : ""}</div>
              </button>
            ))}
            {reports.length === 0 && <div className="p-4 text-center font-mono text-xs text-zinc-400">NO REPORTS IMPORTED</div>}
          </div>
          <div className="flex-1 overflow-auto p-3">
            {selMonth && (
              <div className="flex items-center justify-between mb-2">
                <div className="font-mono text-xs font-bold">{monthLabel(selMonth)} · {rows.length} EMPLOYEES</div>
              <div className="flex items-center gap-2">
                <button onClick={downloadZip} disabled={zipping}
                  className="brut-btn px-3 py-1.5 text-xs flex items-center gap-1 bg-amber-200 hover:bg-amber-300 disabled:opacity-50"
                  data-testid="ctc-report-generate-payslips-btn"
                  title="Generate payslip PDFs for every employee in this month's report and download them as one ZIP.">
                  <Download className="w-3.5 h-3.5" /> {zipping ? "GENERATING…" : "GENERATE PAYSLIPS (ZIP)"}
                </button>
                <button onClick={() => onEmailMonth(selMonth)}
                  className="brut-btn px-3 py-1.5 text-xs flex items-center gap-1 bg-sky-200 hover:bg-sky-300"
                  data-testid="ctc-report-email-payslips-btn"
                  title="Select employees and email them this month's payslip PDF from the HR email account.">
                  <Mail className="w-3.5 h-3.5" /> EMAIL PAYSLIPS
                </button>
                <button
                  onClick={async () => {
                    try {
                      const { data } = await api.post("/attendance/payroll/publish-payslips", { month: selMonth });
                      toast.success(`Published ${data.published} payslips to employee LD accounts (${selMonth})`);
                    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
                  }}
                  className="brut-btn px-3 py-1.5 text-xs flex items-center gap-1 bg-purple-200 hover:bg-purple-300"
                  data-testid="publish-payslips-btn"
                  title="Publish this month's payslips inside the platform — employees see them under Attendance → MY PAYSLIPS (no email).">
                  PUBLISH TO PLATFORM
                </button>
                <PublishHistoryChips />
                <button onClick={() => { onPickMonth(selMonth); onClose(); }} className="brut-btn px-3 py-1.5 text-xs bg-emerald-200 hover:bg-emerald-300" data-testid="ctc-report-use-month-btn">
                  OPEN IN PAYROLL
                </button>
              </div>
              </div>
            )}
            {loading ? <div className="font-mono text-xs text-zinc-400 p-4">LOADING…</div> : (
              <table className="w-full text-xs">
                <thead><tr className="bg-zinc-900 text-white font-mono text-[10px]">
                  <th className="text-left px-2 py-1.5">NAME</th><th className="text-left px-2 py-1.5">EMP ID</th>
                  <th className="text-right px-2 py-1.5">BASIC</th><th className="text-right px-2 py-1.5">GROSS</th>
                  <th className="text-right px-2 py-1.5">PF (E)</th><th className="text-right px-2 py-1.5">ESIC (E)</th>
                  <th className="text-right px-2 py-1.5">PT</th>
                </tr></thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className="border-b border-zinc-100">
                      <td className="px-2 py-1.5 font-bold">{r.employee_name}</td>
                      <td className="px-2 py-1.5 font-mono text-[10px]">{r.employee_id || "—"}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmt(r.basic)}</td>
                      <td className="px-2 py-1.5 text-right font-mono">{fmt(r.gross_monthly)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-red-700">{fmt(r.pf_employee)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-red-700">{fmt(r.esic_employee)}</td>
                      <td className="px-2 py-1.5 text-right font-mono text-red-700">{fmt(r.prof_tax)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </div>
  ), document.body);
}

function EmailPayslipsModal({ open, onClose, month, rows, users, includeEmployer }) {
  const [selected, setSelected] = useState({});
  const [sending, setSending] = useState(false);
  if (!open) return null;
  const candidates = (rows || []).map((p) => {
    const u = (users || []).find((x) => (x.name && x.name === p.user_name) || (p.employee_id && (x.employee_id === p.employee_id || x.employee_code === p.employee_id)));
    return u ? { user_id: u.user_id, name: p.user_name, emp: p.employee_id || u.employee_code || "—", email: u.email || u.personal_email || "" } : null;
  }).filter(Boolean);
  const selectable = candidates.filter((c) => c.email);
  const allSelected = selectable.length > 0 && selectable.every((c) => selected[c.user_id]);
  const toggleAll = () => {
    const next = {};
    if (!allSelected) selectable.forEach((c) => { next[c.user_id] = true; });
    setSelected(next);
  };
  const chosenIds = Object.keys(selected).filter((k) => selected[k]);
  const send = async () => {
    if (chosenIds.length === 0) { toast.error("Select at least one employee"); return; }
    setSending(true);
    try {
      const { data } = await api.post("/attendance/payroll/email-payslips", {
        month, user_ids: chosenIds, include_employer_contribution: includeEmployer,
      });
      if (data.sent_count > 0) toast.success(`${data.sent_count} payslip${data.sent_count > 1 ? "s" : ""} emailed from ${data.from}${data.failed_count ? ` · ${data.failed_count} failed` : ""}`);
      if (data.failed_count > 0) {
        const first = data.failed[0];
        toast.error(`Failed: ${first.name || first.user_id} — ${first.error}`);
      }
      if (data.sent_count > 0 && data.failed_count === 0) onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to send payslips"); }
    setSending(false);
  };
  return createPortal((
    <div className="fixed inset-0 z-[90] bg-black/50 flex items-start justify-center p-4 pt-[76px] overflow-y-auto" data-testid="email-payslips-modal">
      <div className="brut-card bg-white w-full max-w-lg max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-4 border-b-2 border-zinc-900">
          <div>
            <div className="font-heading font-black text-lg flex items-center gap-2"><Mail className="w-4 h-4" /> EMAIL PAYSLIPS · {month}</div>
            <div className="text-[11px] font-mono text-zinc-500 mt-0.5">Sent from the configured HR email · Employer contribution: {includeEmployer ? "INCLUDED" : "EXCLUDED"}</div>
          </div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5" data-testid="email-payslips-close-btn"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          <label className="flex items-center gap-2 pb-2 border-b border-zinc-200 cursor-pointer">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="w-4 h-4 accent-emerald-700" data-testid="email-payslips-select-all" />
            <span className="font-mono text-xs font-bold">SELECT ALL ({selectable.length})</span>
          </label>
          {candidates.map((c) => (
            <label key={c.user_id} className={`flex items-center gap-2 py-1.5 border-b border-zinc-100 ${c.email ? "cursor-pointer" : "opacity-40"}`} data-testid={`email-payslip-row-${c.name}`}>
              <input type="checkbox" disabled={!c.email} checked={!!selected[c.user_id]}
                onChange={(e) => setSelected({ ...selected, [c.user_id]: e.target.checked })}
                className="w-4 h-4 accent-emerald-700" />
              <span className="text-sm font-bold flex-1">{c.name}</span>
              <span className="font-mono text-[10px] text-zinc-500">{c.emp}</span>
              <span className="font-mono text-[10px] text-zinc-600">{c.email || "no email"}</span>
            </label>
          ))}
          {candidates.length === 0 && <div className="text-center py-8 text-zinc-400 font-mono text-xs">NO PAYROLL EMPLOYEES FOR {month}</div>}
        </div>
        <div className="p-4 border-t-2 border-zinc-900 flex justify-between items-center">
          <span className="font-mono text-xs">{chosenIds.length} selected</span>
          <button onClick={send} disabled={sending || chosenIds.length === 0}
            className="brut-btn px-4 py-2 text-xs flex items-center gap-1 bg-emerald-300 hover:bg-emerald-400 disabled:opacity-50"
            data-testid="email-payslips-send-btn">
            <Mail className="w-3.5 h-3.5" /> {sending ? "SENDING..." : "SEND PAYSLIPS"}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

function PayrollTab({ month, setMonth }) {
  const [payroll, setPayroll] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [includeEmployer, setIncludeEmployer] = useState(true);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showCtcReports, setShowCtcReports] = useState(false);
  const [ctcStatus, setCtcStatus] = useState(null);
  const salaryImportRef = useRef(null);
  const structureImportRef = useRef(null);
  const ctcImportRef = useRef(null);
  const loadCtcStatus = async (m) => {
    try {
      const { data } = await api.get("/attendance/payroll/ctc-report-status", { params: { month: m } });
      setCtcStatus(data);
    } catch { setCtcStatus(null); }
  };
  useEffect(() => { loadCtcStatus(month); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const onCtcImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("month", month);
    try {
      const { data } = await api.post("/attendance/payroll/import-ctc-breakdown", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`CTC report ${data.month}: ${data.imported} employees imported${data.replaced ? ` (replaced previous import)` : ""}${(data.errors || []).length ? ` · ${data.errors.length} errors` : ""}`);
      if (data.month !== month) toast.info(`Report is for ${data.month} (detected from file title) — payslips for that month will use it`);
      loadCtcStatus(month);
    } catch (err) { toast.error(err.response?.data?.detail || "CTC report import failed"); }
    e.target.value = "";
  };
  const load = async () => {
    setLoading(true); setError(null);
    try {
      const [pRes, uRes] = await Promise.allSettled([
        api.get("/attendance/payroll/monthly", { params: { month } }),
        api.get("/users"),
      ]);
      if (pRes.status === "rejected") {
        const detail = pRes.reason?.response?.data?.detail || pRes.reason?.message || "Unknown error";
        setError(`Payroll fetch failed (${pRes.reason?.response?.status || "network"}): ${detail}`);
        setPayroll(null);
      } else {
        setPayroll(pRes.value.data);
      }
      setUsers(uRes.status === "fulfilled" ? (uRes.value.data || []) : []);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load payroll");
      setPayroll(null);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !payroll) return (
    <div className="font-mono text-xs text-zinc-500 flex items-center gap-3" data-testid="payroll-loading">
      <span>Loading payroll for {month}...</span>
    </div>
  );
  if (error) return (
    <div className="brut-card p-4 border-red-400 bg-red-50" data-testid="payroll-error">
      <div className="label-mono text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> PAYROLL LOAD FAILED</div>
      <div className="text-sm font-mono mt-2 text-red-800 whitespace-pre-wrap break-words">{error}</div>
      <div className="text-xs font-mono mt-2 text-zinc-600">
        Tip: if the payroll shows blank, first import employees via ATTENDANCE &gt; EMPLOYEES &gt; IMPORT SALARYBOX.
      </div>
      <button onClick={load} className="brut-btn px-3 py-1.5 text-xs mt-3" data-testid="payroll-retry-btn">RETRY</button>
    </div>
  );
  if (!payroll) return <div className="font-mono text-xs text-zinc-500">No payroll data.</div>;

  const downloadPayslip = async (p) => {
    // find matching user_id (by name or employee_id)
    const u = users.find((x) => (x.name && x.name === p.user_name) || (p.employee_id && (x.employee_id === p.employee_id || x.employee_code === p.employee_id)));
    if (!u) { toast.error("Employee not found in directory"); return; }
    try {
      const res = await api.get(`/attendance/payroll/${u.user_id}/payslip`, { params: { month, include_employer_contribution: includeEmployer }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = `payslip_${p.user_name}_${month}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Failed to generate payslip"); }
  };

  // ---- Payroll-tab shortcut: import the SalaryBox Monthly Wage template
  // directly from here so admins don't need to switch to the Employees tab.
  // Uses the same auto-detecting endpoint so BOTH sheet formats work.
  const onSalaryImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/employees/import-hr-master", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const created = data.created || 0;
      const updated = data.updated || 0;
      const errCount = (data.errors || []).length;
      if (created === 0 && updated === 0 && errCount > 0) {
        const firstErr = data.errors[0];
        const msg = typeof firstErr === "string" ? firstErr : (firstErr?.error || JSON.stringify(firstErr));
        toast.error(`Salary import failed: 0 imported, ${errCount} errors. First: ${msg.slice(0, 220)}`);
      } else {
        toast.success(`Salary import: ${created} new · ${updated} updated${errCount ? ` · ${errCount} errors` : ""}`);
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Salary import failed"); }
    e.target.value = "";
  };

  // ---- LD Salary Structure Template (22-column exact CTC per employee) ----
  // Uploads the artifact HR maintains in Excel and writes the EXACT figures
  // into every user document. Payslips and Offer/Appointment letters then
  // read the exact snapshot instead of formula-based defaults.
  const onStructureImport = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/payroll/import-salary-structure", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const matched = data.matched || 0;
      const unmatched = data.unmatched_count || 0;
      const errs = data.error_count || 0;
      if (matched === 0) {
        toast.error(`Salary structure import: 0 matched · ${unmatched} unmatched · ${errs} errors`);
      } else {
        toast.success(`Salary structure: ${matched} employees updated${unmatched ? ` · ${unmatched} not in directory` : ""}${errs ? ` · ${errs} errors` : ""}`);
      }
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Structure import failed"); }
    e.target.value = "";
  };

  const downloadStructureTemplate = async (prefill) => {
    try {
      const res = await api.get("/payroll/salary-structure/template", { params: { prefill }, responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = u;
      a.download = prefill ? "ld_salary_structure_export.xlsx" : "ld_salary_structure_blank.xlsx";
      a.click();
      URL.revokeObjectURL(u);
    } catch (err) { toast.error("Template download failed"); }
  };

  return (
    <div className="space-y-3">
      <div className="brut-card p-5 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-end gap-4 flex-wrap">
          <div><div className="label-mono">MONTH</div><input type="month" className="brut-input px-3 py-1.5 text-sm font-mono mt-1" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="payroll-month-input" /></div>
          <div className="flex flex-col gap-1">
            <div className="label-mono">SALARY EXCEL</div>
            <div className="flex gap-2">
              <input ref={salaryImportRef} type="file" accept=".xlsx,.csv" onChange={onSalaryImport} className="hidden" data-testid="payroll-salary-import-input" />
              <button
                onClick={() => salaryImportRef.current && salaryImportRef.current.click()}
                className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-amber-200 hover:bg-amber-300"
                data-testid="payroll-import-salary-btn"
                title="Import SalaryBox Monthly Wage Template — auto-fills Basic, HRA, DA, Conveyance, LTA, Medical, Night-Shift, Special allowances + PF/ESIC/PT/LWF flags for every listed employee. Same components are then auto-pulled into Offer / Appointment / Increment letters."
              >
                <Upload className="w-3.5 h-3.5" /> IMPORT SALARY EXCEL
              </button>
              <button
                onClick={async () => {
                  const token = localStorage.getItem("session_token");
                  const res = await api.get("/employees/template", { params: token ? { auth: token } : {}, responseType: "blob" });
                  const u = URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement("a"); a.href = u; a.download = "salarybox_salary_template.xlsx"; a.click();
                  URL.revokeObjectURL(u);
                }}
                className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1"
                data-testid="payroll-template-btn"
                title="Download the SalaryBox column template"
              >
                <FileSpreadsheet className="w-3.5 h-3.5" /> TEMPLATE
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="label-mono">LD SALARY STRUCTURE</div>
            <div className="flex gap-2">
              <input ref={structureImportRef} type="file" accept=".xlsx,.xls,.csv" onChange={onStructureImport} className="hidden" data-testid="payroll-structure-import-input" />
              <button
                onClick={() => structureImportRef.current && structureImportRef.current.click()}
                className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-emerald-200 hover:bg-emerald-300"
                data-testid="payroll-import-structure-btn"
                title="Upload the LD Employee Salary Structure Template (22-column XLSX). Writes the EXACT Basic/HRA/PF/ESIC/In-Hand/CTC figures onto every matched employee, overriding formula-based defaults. Same values then auto-populate Offer / Appointment / Increment letters."
              >
                <Upload className="w-3.5 h-3.5" /> IMPORT LD STRUCTURE
              </button>
              <button
                onClick={() => downloadStructureTemplate(true)}
                className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1"
                data-testid="payroll-structure-export-btn"
                title="Download every current employee's exact salary structure as an Excel file — edit and re-upload to update figures."
              >
                <Download className="w-3.5 h-3.5" /> EXPORT
              </button>
              <button
                onClick={() => downloadStructureTemplate(false)}
                className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1"
                data-testid="payroll-structure-blank-btn"
                title="Download a blank 22-column template with headers only."
              >
                <FileSpreadsheet className="w-3.5 h-3.5" /> BLANK
              </button>
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <div className="label-mono">CTC BREAKDOWN (PAYSLIPS)</div>
            <div className="flex items-center gap-2">
              <input ref={ctcImportRef} type="file" accept=".xlsx,.xls" onChange={onCtcImport} className="hidden" data-testid="payroll-ctc-import-input" />
              <button
                onClick={() => ctcImportRef.current && ctcImportRef.current.click()}
                className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-violet-200 hover:bg-violet-300"
                data-testid="payroll-import-ctc-btn"
                title="Upload the SalaryBox 'CTC Breakdown Report <Month Year>' XLSX. Payslips for that month (PDF + email) will use these EXACT monthly figures — earnings, PF, ESIC, PT — instead of the employee defaults. The month is auto-detected from the report title."
              >
                <Upload className="w-3.5 h-3.5" /> IMPORT CTC REPORT
              </button>
              <span
                className={`font-mono text-[10px] px-2 py-1 border border-zinc-900 font-bold ${ctcStatus?.imported ? "bg-emerald-200" : "bg-zinc-100 text-zinc-500"}`}
                data-testid="ctc-report-status-badge"
                title={ctcStatus?.imported ? `Imported ${ctcStatus.imported_at || ""}` : "No CTC report imported for this month — payslips will use employee defaults"}
              >
                {ctcStatus?.imported ? `✓ ${ctcStatus.count} EMP` : "NOT IMPORTED"}
              </span>
              <button
                onClick={() => setShowCtcReports(true)}
                className="brut-btn-secondary px-2 py-1.5 text-[10px] font-mono font-bold"
                data-testid="payroll-view-ctc-reports-btn"
                title="See all imported CTC reports — which months, when, by whom, and the exact per-employee figures."
              >
                VIEW REPORTS
              </button>
            </div>
          </div>
        </div>
        <div className="flex flex-col gap-2 items-start">
          <label className="flex items-center gap-2 cursor-pointer select-none" title="When ticked, generated payslips include the Employer Contribution block (PF / ESIC / Gratuity — part of CTC).">
            <input
              type="checkbox"
              checked={includeEmployer}
              onChange={(e) => setIncludeEmployer(e.target.checked)}
              className="w-4 h-4 accent-emerald-700 border-zinc-900"
              data-testid="payslip-include-employer-checkbox"
            />
            <span className="font-mono text-xs font-bold">EMPLOYER CONTRIBUTION IN PAYSLIP</span>
          </label>
          <button
            onClick={() => setShowEmailModal(true)}
            className="brut-btn px-3 py-2 text-xs flex items-center gap-1 bg-sky-200 hover:bg-sky-300"
            data-testid="payroll-email-payslips-btn"
            title="Select employees and email them their payslip PDF directly from the configured HR email account."
          >
            <Mail className="w-3.5 h-3.5" /> EMAIL PAYSLIPS
          </button>
        </div>
        <div className="grid grid-cols-3 gap-4 text-right">
          <div><div className="label-mono">GROSS</div><div className="font-heading text-xl font-black flex items-center gap-1 justify-end"><IndianRupee className="w-4 h-4" />{(payroll.total_gross_inr || 0).toLocaleString("en-IN")}</div></div>
          <div><div className="label-mono">PF (Employee)</div><div className="font-heading text-xl font-black flex items-center gap-1 justify-end text-orange-700"><IndianRupee className="w-4 h-4" />{(payroll.total_pf_employee_inr || 0).toLocaleString("en-IN")}</div></div>
          <div><div className="label-mono">NET PAYABLE · {payroll.headcount}</div><div className="font-heading text-2xl font-black flex items-center gap-1 justify-end text-green-700"><IndianRupee className="w-5 h-5" />{(payroll.total_net_payable_inr || 0).toLocaleString("en-IN")}</div></div>
        </div>
      </div>
      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead><tr className="bg-zinc-900 text-white">
            <Th>Name</Th><Th>Emp ID</Th><Th>Team</Th>
            <Th align="center">P/H/L/A</Th><Th align="center">Late</Th><Th align="center">Early-Out</Th>
            <Th align="right">Gross</Th><Th align="right">PF (E)</Th><Th align="right">ESIC (E)</Th><Th align="right">Net Pay</Th>
            <Th align="center">Flags</Th>
            <Th align="center">Payslip</Th>
          </tr></thead>
          <tbody>
            {(payroll.rows || []).length === 0 && (<tr><td colSpan={12} className="text-center py-10 text-zinc-400 font-mono text-xs">NO PAYROLL DATA FOR {payroll.month}</td></tr>)}
            {(payroll.rows || []).map((p) => (
              <tr key={`${p.user_name}-${p.employee_id || ""}`} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`payroll-row-${p.user_name}`}>
                <Td className="font-bold">{p.user_name}</Td>
                <Td><span className="font-mono text-xs">{p.employee_id || "—"}</span></Td>
                <Td><span className="font-mono text-xs">{p.team || "—"}</span></Td>
                <Td align="center"><span className="font-mono text-xs">{p.days_present}/{p.days_half}/{p.days_leave}/{p.days_absent}</span></Td>
                <Td align="center">{p.late_marks > 0 ? <span className="font-mono text-xs bg-red-100 border border-zinc-900 px-1 font-bold">{p.late_marks}</span> : <span className="text-zinc-400 text-xs">—</span>}</Td>
                <Td align="center">{(p.early_out_marks || 0) > 0 ? <span className="font-mono text-xs bg-orange-100 border border-zinc-900 px-1 font-bold">{p.early_out_marks}</span> : <span className="text-zinc-400 text-xs">—</span>}</Td>
                <Td align="right"><span className="font-mono">₹{(p.monthly_salary_inr || 0).toLocaleString("en-IN")}</span></Td>
                <Td align="right"><span className="font-mono text-orange-700">₹{(p.pf_employee_inr || 0).toLocaleString("en-IN")}</span></Td>
                <Td align="right"><span className="font-mono text-orange-700">₹{(p.esic_employee_inr || 0).toLocaleString("en-IN")}</span></Td>
                <Td align="right"><span className="font-mono font-bold text-green-700">₹{(p.net_pay_inr || 0).toLocaleString("en-IN")}</span></Td>
                <Td align="center"><PayrollFlagsToggle row={p} users={users} onChanged={load} /></Td>
                <Td align="center"><button onClick={() => downloadPayslip(p)} className="brut-btn-secondary px-2 py-1 text-[10px] font-mono" data-testid={`payslip-${p.user_name}`}>PDF</button></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs font-mono text-zinc-500">PF capped on basic ≤ ₹15,000 · ESIC applies when monthly gross ≤ ₹21,000 · Late: check-in &gt; rules.late_cutoff · Early-out: check-out &lt; rules.early_punchout_cutoff.</p>
      <EmailPayslipsModal open={showEmailModal} onClose={() => setShowEmailModal(false)} month={month} rows={payroll.rows} users={users} includeEmployer={includeEmployer} />
      <CtcReportsModal open={showCtcReports} onClose={() => setShowCtcReports(false)} onPickMonth={(m) => setMonth(m)}
        onEmailMonth={(m) => { setMonth(m); setShowCtcReports(false); setShowEmailModal(true); }}
        includeEmployer={includeEmployer} />
    </div>
  );
}

// ============================== RULES ==============================
function RulesTab() {
  const [rules, setRules] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => { const { data } = await api.get("/attendance/rules"); setRules(data); })(); }, []);
  if (!rules) return <div className="font-mono text-xs text-zinc-500">Loading...</div>;
  const save = async () => {
    setSaving(true);
    try { await api.patch("/attendance/rules", rules); toast.success("Rules updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    setSaving(false);
  };
  return (
    <div className="brut-card p-5 max-w-2xl space-y-4">
      <div className="flex items-center gap-2"><Settings className="w-5 h-5" /><h3 className="font-heading text-xl font-black">Attendance Rules</h3></div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <label className="block">
          <div className="label-mono mb-1">PUNCH BUFFER (MINUTES)</div>
          <input type="number" min="0" className="brut-input w-full px-3 py-2 text-sm font-mono" value={rules.buffer_minutes} onChange={(e) => setRules({ ...rules, buffer_minutes: parseInt(e.target.value) || 0 })} data-testid="rules-buffer-input" />
          <div className="label-mono mt-1">Time deducted from check-in/out diff during auto hours computation.</div>
        </label>
        <label className="block">
          <div className="label-mono mb-1">LATE MARK CUT-OFF (HH:MM)</div>
          <input className="brut-input w-full px-3 py-2 text-sm font-mono" value={rules.late_cutoff} onChange={(e) => setRules({ ...rules, late_cutoff: e.target.value })} data-testid="rules-late-input" />
          <div className="label-mono mt-1">Any check-in after this time triggers a LATE flag.</div>
        </label>
        <label className="block">
          <div className="label-mono mb-1">EARLY PUNCH-OUT CUT-OFF</div>
          <input className="brut-input w-full px-3 py-2 text-sm font-mono" value={rules.early_punchout_cutoff} onChange={(e) => setRules({ ...rules, early_punchout_cutoff: e.target.value })} />
        </label>
        <label className="block">
          <div className="label-mono mb-1">EXPECTED HOURS / DAY</div>
          <input type="number" step="0.5" className="brut-input w-full px-3 py-2 text-sm font-mono" value={rules.expected_hours_per_day} onChange={(e) => setRules({ ...rules, expected_hours_per_day: parseFloat(e.target.value) || 8 })} />
        </label>
      </div>
      <button disabled={saving} onClick={save} className="brut-btn px-4 py-2 text-sm disabled:opacity-50" data-testid="rules-save-btn">{saving ? "SAVING..." : "SAVE RULES"}</button>
    </div>
  );
}

const Th = ({ children, align = "left" }) => <th className={`text-${align} px-3 py-2 label-mono text-white border-b border-zinc-900 whitespace-nowrap`}>{children}</th>;
const Td = ({ children, align = "left", className = "" }) => <td className={`text-${align} px-3 py-2.5 ${className}`}>{children}</td>;

// -------------------------------------------------------------------
// PayrollFlagsToggle — inline dropdown to flip a single employee's
// statutory gates + night-shift eligibility straight from the payroll
// table.  Hits POST /api/payroll/salary-structure/{uid}/flags so payslip
// generation reflects the new flags on the very next PDF render.
function PayrollFlagsToggle({ row, users, onChanged }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  // Resolve user_id from the payroll row -> full user profile
  const emp = React.useMemo(() => {
    if (!row.employee_id) return null;
    return (users || []).find((u) =>
      (u.employee_code || u.employee_id || "").toString().toUpperCase() ===
      row.employee_id.toString().toUpperCase(),
    ) || null;
  }, [row.employee_id, users]);

  const currentType = (emp?.employment_type || "Regular").toLowerCase();
  const isContract = currentType === "contract";
  const nightOn = !!emp?.night_shift_eligible;

  const patchFlags = async (body, label) => {
    if (!emp) { toast.error("Employee not in directory"); return; }
    setSaving(true);
    try {
      await api.patch(`/payroll/salary-structure/${emp.user_id}/flags`, body);
      toast.success(label + " updated");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || `Failed to update ${label}`); }
    setSaving(false);
  };

  if (!emp) {
    return <span className="text-zinc-300 text-[10px] font-mono">—</span>;
  }
  return (
    <div className="relative inline-block" data-testid={`payroll-flags-${row.user_name}`}>
      <button
        onClick={() => setOpen((s) => !s)}
        className="brut-btn-secondary px-2 py-1 text-[10px] font-mono flex items-center gap-1"
        data-testid={`payroll-flags-btn-${row.user_name}`}
      >
        {isContract ? <span className="bg-blue-200 border border-zinc-900 px-1">C</span> : null}
        {nightOn   ? <span className="bg-purple-200 border border-zinc-900 px-1">NS</span> : null}
        {!isContract && !nightOn ? <span className="text-zinc-500">FLAGS</span> : null}
        <Settings className="w-3 h-3" />
      </button>
      {open && (
        <div className="absolute right-0 z-40 mt-1 w-64 brut-card p-3 bg-white shadow-xl text-left" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center justify-between mb-2">
            <div className="label-mono">STATUTORY / SHIFT FLAGS</div>
            <button onClick={() => setOpen(false)} className="text-zinc-400 hover:text-zinc-900"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="text-[10px] font-mono text-zinc-500 mb-3 truncate">{emp.name} · {emp.employee_code || emp.employee_id}</div>
          <label className="block mb-2">
            <div className="label-mono text-zinc-600 mb-1">EMPLOYMENT TYPE</div>
            <select
              className="brut-input w-full px-2 py-1 text-xs font-mono"
              value={isContract ? "Contract" : "Regular"}
              disabled={saving}
              onChange={(e) => patchFlags({ employment_type: e.target.value }, "Employment type")}
              data-testid={`payroll-emp-type-${row.user_name}`}
            >
              <option value="Regular">Regular (PF / ESIC / PT apply)</option>
              <option value="Contract">Contract (no PF / ESIC / PT)</option>
            </select>
          </label>
          <label className="flex items-center gap-2 text-xs font-mono mb-2 cursor-pointer">
            <input
              type="checkbox" className="w-4 h-4"
              checked={nightOn} disabled={saving}
              onChange={(e) => patchFlags({ night_shift_eligible: e.target.checked }, "Night shift")}
              data-testid={`payroll-night-toggle-${row.user_name}`}
            />
            <span>Eligible for Night Shift ₹1,000/mo</span>
          </label>
          <div className="border-t border-zinc-200 pt-2 mt-2">
            <div className="label-mono text-zinc-600 mb-1">RAW GATES (advanced)</div>
            {[
              { k: "pf_enabled",   label: "PF Enabled" },
              { k: "esic_enabled", label: "ESIC Enabled" },
              { k: "pt_enabled",   label: "PT Enabled" },
            ].map(({ k, label }) => (
              <label key={k} className="flex items-center gap-2 text-xs font-mono cursor-pointer mb-1">
                <input
                  type="checkbox" className="w-3.5 h-3.5"
                  checked={!!emp[k]} disabled={saving}
                  onChange={(e) => patchFlags({ [k]: e.target.checked }, label)}
                  data-testid={`payroll-flag-${k}-${row.user_name}`}
                />
                {label}
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}


export function MyPayslipsTab() {
  const now = new Date();
  const [start, setStart] = useState(`${now.getFullYear()}-01`);
  const [end, setEnd] = useState(now.toISOString().slice(0, 7));
  const [slips, setSlips] = useState(null);

  useEffect(() => {
    api.get("/my/payslips", { params: { start, end } }).then((r) => setSlips(r.data || [])).catch(() => setSlips([]));
  }, [start, end]);

  const download = async (month) => {
    const tid = toast.loading("Preparing payslip…");
    try {
      const res = await api.get(`/my/payslips/${month}/pdf`, { responseType: "blob" });
      const u = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = u; a.download = `payslip_${month}.pdf`; a.click();
      URL.revokeObjectURL(u);
      toast.success("Payslip downloaded", { id: tid });
    } catch (e) { toast.error(e.response?.data?.detail || "Download failed", { id: tid }); }
  };

  return (
    <div className="brut-card p-4 mt-4" data-testid="my-payslips-tab">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="label-mono">MY SALARY SLIPS — published by admin, month-wise</div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="font-mono text-[10px]">FROM</label>
          <input type="month" className="brut-input px-2 py-1.5 text-xs font-mono" value={start} onChange={(e) => setStart(e.target.value)} data-testid="payslip-range-start" />
          <label className="font-mono text-[10px]">TO</label>
          <input type="month" className="brut-input px-2 py-1.5 text-xs font-mono" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="payslip-range-end" />
        </div>
      </div>
      {slips === null && <div className="font-mono text-xs text-zinc-500">Loading…</div>}
      {slips && slips.length === 0 && (
        <div className="text-center py-8 text-zinc-400 font-mono text-xs">NO PAYSLIPS PUBLISHED FOR THIS RANGE YET — THEY APPEAR HERE ONCE ADMIN GENERATES THEM</div>
      )}
      {slips && slips.length > 0 && (
        <div className="grid gap-2" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(210px, 1fr))" }}>
          {slips.map((s) => (
            <div key={s.month} className="border border-zinc-900 p-3 flex items-center justify-between gap-2 bg-emerald-50" data-testid={`payslip-card-${s.month}`}>
              <div>
                <div className="font-heading font-black text-sm">{new Date(s.month + "-01").toLocaleString("en", { month: "long", year: "numeric" })}</div>
                <div className="font-mono text-[9px] text-zinc-500">via {s.via} · {(s.published_at || "").slice(0, 10)}</div>
              </div>
              <button onClick={() => download(s.month)} className="brut-btn px-2.5 py-1.5 text-xs" data-testid={`download-payslip-${s.month}`}>
                <Download className="w-3.5 h-3.5" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PublishHistoryChips() {
  const [hist, setHist] = useState([]);
  useEffect(() => {
    api.get("/attendance/payroll/publish-history").then((r) => setHist(r.data || [])).catch(() => {});
  }, []);
  if (!hist.length) return <span className="font-mono text-[9px] text-zinc-400">NO MONTHS PUBLISHED YET</span>;
  return (
    <span className="flex flex-wrap items-center gap-1" data-testid="publish-history-chips" title="Months already published to employee accounts">
      <span className="font-mono text-[9px] text-zinc-500">PUBLISHED:</span>
      {hist.slice(0, 8).map((h) => (
        <span key={h.month} className="font-mono text-[9px] font-bold px-1.5 py-0.5 border border-purple-700 bg-purple-100 text-purple-800" data-testid={`pub-hist-${h.month}`}>
          {h.month} · {h.count}
        </span>
      ))}
    </span>
  );
}
